/**
 * DatasetForm - Form for creating/editing datasets
 * 
 * CHANGE HISTORY:
 * - 251201_0505 danielbeer: Created dataset form component
 */

import React, { useState } from 'react';
import type { Dataset, DatasetConfig } from '../types/DatasetTypes';
import './DatasetForm.css';

export interface DatasetFormProps {
  dataset?: Dataset;
  onSave: (config: DatasetConfig) => void;
  onCancel: () => void;
  isCreating?: boolean;
}

export function DatasetForm({
  dataset,
  onSave,
  onCancel,
  isCreating = false
}: DatasetFormProps) {
  const [name, setName] = useState(dataset?.name || '');
  const [description, setDescription] = useState(dataset?.description || '');
  const [tags, setTags] = useState(dataset?.tags?.join(', ') || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Please enter a dataset name');
      return;
    }

    const config: DatasetConfig = {
      name: name.trim(),
      description: description.trim() || undefined,
      status: 'draft',
      sources: dataset?.sources || [],
      fields: dataset?.fields,
      cacheConfig: dataset?.cacheConfig,
      tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined
    };

    onSave(config);
  };

  return (
    <div className="dataset-form">
      <h3>{isCreating ? 'Create New Dataset' : 'Edit Dataset'}</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="dataset-name">
            Dataset Name <span className="required">*</span>
          </label>
          <input
            id="dataset-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Customer Contacts"
            required
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="dataset-description">Description</label>
          <textarea
            id="dataset-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this dataset contains..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dataset-tags">Tags</label>
          <input
            id="dataset-tags"
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., customers, contacts, sales"
          />
          <span className="form-hint">Comma-separated tags</span>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {isCreating ? 'Create Dataset' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
